import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { DuplicateCandidate, EntityDefinition } from '../api';
import { ErrorNotice } from '../components/ErrorNotice';

// Reviewing probable duplicates.
//
// The screen is built around a claim and its evidence, because that is what a
// reviewer is actually judging: not "is this score high" but "are these two
// records the same company". So each pair shows both records as they were when
// scanned, and the reason in words — a score alone is a number nobody can act
// on.

export function DuplicatesPage() {
  const { tenantId, entity } = useParams<{ tenantId: string; entity: string }>();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [definition, setDefinition] = useState<EntityDefinition | null>(null);
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanSummary, setScanSummary] = useState<string | null>(null);

  const status = searchParams.get('status') ?? 'pending';

  useEffect(() => {
    if (!api) return;
    api
      .get<{ entities: EntityDefinition[] }>('/entities')
      .then(({ entities }) =>
        setDefinition(
          entities.find((e) => e.name.toLowerCase() === entity?.toLowerCase()) ?? null
        )
      )
      .catch(() => setDefinition(null));
  }, [api, entity]);

  const load = useCallback(async () => {
    if (!api || !tenantId || !entity) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<{ candidates: DuplicateCandidate[] }>(
        `/tenants/${tenantId}/duplicates?entity=${encodeURIComponent(entity)}&status=${status}`
      );
      setCandidates(result.candidates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load duplicates.');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, entity, status]);

  useEffect(() => {
    void load();
  }, [load]);

  async function scan() {
    if (!api || !tenantId || !entity || scanning) return;
    setScanning(true);
    setError(null);
    setScanSummary(null);
    try {
      const result = await api.post<{
        recordsScanned: number;
        truncated: boolean;
        found: number;
        alreadyResolved: number;
      }>(`/tenants/${tenantId}/duplicates/scan`, { entityType: entity });

      setScanSummary(
        `Scanned ${result.recordsScanned} records and found ${result.found} probable ` +
          `duplicate${result.found === 1 ? '' : 's'}` +
          (result.alreadyResolved
            ? `. ${result.alreadyResolved} pair${result.alreadyResolved === 1 ? '' : 's'} you have already resolved were left alone.`
            : '.') +
          (result.truncated
            ? ' The scan stopped at its record limit, so there may be more beyond it.'
            : '')
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The scan could not be run.');
    } finally {
      setScanning(false);
    }
  }

  async function dismiss(candidate: DuplicateCandidate) {
    if (!api || !tenantId) return;
    setError(null);
    try {
      await api.post(
        `/tenants/${tenantId}/duplicates/${candidate.duplicateCandidateId}/dismiss`,
        {}
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not dismiss the pair.');
    }
  }

  /** Confidence, in words. A reviewer should not have to interpret 0.8417. */
  function confidence(score: number): { label: string; className: string } {
    if (score >= 0.95) return { label: 'Very likely', className: 'score-high' };
    if (score >= 0.88) return { label: 'Likely', className: 'score-mid' };
    return { label: 'Possible', className: 'score-low' };
  }

  const label = definition?.label ?? entity;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={`/tenants/${tenantId}`} className="muted small">
            ← Instance
          </Link>
          <h1>Duplicate {label}</h1>
        </div>
        <div className="toolbar">
          <Link
            to={`/tenants/${tenantId}/entities/${entity}`}
            className="button-link"
          >
            Back to grid
          </Link>
          <button
            type="button"
            className="primary"
            onClick={() => void scan()}
            disabled={scanning}
          >
            {scanning ? 'Scanning…' : 'Scan for duplicates'}
          </button>
        </div>
      </div>

      <ErrorNotice message={error} onRetry={() => void load()} />

      {scanSummary && <div className="notice success">{scanSummary}</div>}

      <div className="toolbar">
        {(['pending', 'dismissed', 'merged'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'primary' : undefined}
            onClick={() => setSearchParams({ status: value })}
          >
            {value[0]?.toUpperCase()}
            {value.slice(1)}
          </button>
        ))}
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && candidates.length === 0 && (
        <div className="card empty">
          <p className="muted">
            {status === 'pending'
              ? `No duplicate ${label?.toLowerCase()} are waiting for review. Run a scan to look again.`
              : `Nothing ${status}.`}
          </p>
        </div>
      )}

      {candidates.map((candidate) => {
        const score = confidence(candidate.matchScore);
        return (
          <div className="card duplicate-pair" key={candidate.duplicateCandidateId}>
            <div className="duplicate-head">
              <span className={`score-pill ${score.className}`}>
                {score.label} · {Math.round(candidate.matchScore * 100)}%
              </span>
              <span className="muted small">{candidate.matchReason}</span>
            </div>

            <div className="duplicate-records">
              <div className="duplicate-record">
                <span className="muted small">Record A</span>
                <strong className="mono">{candidate.recordIdA}</strong>
                <span>{candidate.summaryA}</span>
              </div>
              <div className="duplicate-record">
                <span className="muted small">Record B</span>
                <strong className="mono">{candidate.recordIdB}</strong>
                <span>{candidate.summaryB}</span>
              </div>
            </div>

            {candidate.status === 'pending' && (
              <div className="form-actions">
                <button type="button" onClick={() => void dismiss(candidate)}>
                  Not a duplicate
                </button>
                {/* Merge is its own screen rather than an action here: it
                    needs both records in full and a count of the documents
                    hanging off each, which is several metered reads. Spending
                    those on every pair in a list nobody has opened would be
                    careless with the customer's API budget. */}
                <Link
                  className="button-link primary"
                  to={`/tenants/${tenantId}/entities/${entity}/duplicates/${candidate.duplicateCandidateId}/merge`}
                >
                  Review and merge
                </Link>
              </div>
            )}
          </div>
        );
      })}

      {candidates.length > 0 && status === 'pending' && (
        <p className="muted small">
          Dismissing a pair records your judgment, and a later scan will not raise
          it again. Merging keeps one record and retires the other — it is
          reversible, and the merge screen shows how much history each record
          carries before you decide.
        </p>
      )}
    </>
  );
}
